import {
  prisma,
  RestrictionType,
  RestrictionScope,
  RestrictionTargetType,
  EventRestriction,
} from "client";
import { getEnabledEventRestrictions } from "./index";
import type { SelectedItemType } from "../order-v2/types";

interface ProfileIdentifier {
  email?: string | null;
  phoneNumber?: string | null;
}

interface OrderItemQuantity {
  ticketId?: string;
  entityId?: string;
  quantity: number;
}

/**
 * Validate order against event restrictions
 * @param eventId - Event ID
 * @param profileId - Profile ID (can be null/undefined for first-time buyers)
 * @param orderType - Order type (TICKET or ENTITY)
 * @param itemQuantities - Array of ticket/entity IDs and quantities being purchased
 * @returns Validation result with error message if invalid
 */
export const validateOrderRestrictions = async (
  restrictions: EventRestriction[],
  profileId: string | null | undefined,
  orderType: "TICKET" | "ENTITY",
  itemQuantities: SelectedItemType[]
): Promise<{ valid: boolean; error?: string }> => {
  try {
    if (restrictions.length === 0) {
      return { valid: true };
    }

    const profile = profileId
      ? await prisma.profile.findUnique({
          where: {
            id: profileId,
          },
          select: {
            email: true,
            phoneNumber: true,
          },
        })
      : null;
    const existingCountCache = new Map<string, Promise<number>>();

    // Check each restriction type
    for (const restriction of restrictions) {
      // Skip if restriction is not enabled (shouldn't happen due to getEnabledEventRestrictions, but double-check)
      if (!restriction.enabled) {
        continue;
      }

      // Calculate total items being purchased that match this restriction
      let newItemCount = 0;
      for (const itemQty of itemQuantities) {
        // Determine the item ID based on order type
        const itemId =
          orderType === "TICKET" ? itemQty.ticketId : itemQty.entityId;

        // Skip if no itemId (might be invitation or invalid)
        if (!itemId) {
          continue;
        }

        // Check if this item matches the restriction scope
        // Only validate if the restriction scope matches the order type
        const isTicketRestriction =
          restriction.scope === RestrictionScope.ALL_TICKETS ||
          restriction.scope === RestrictionScope.SPECIFIC_TICKETS ||
          restriction.scope === RestrictionScope.TICKET_CATEGORY;

        const isEntityRestriction =
          restriction.scope === RestrictionScope.ALL_ENTITIES ||
          restriction.scope === RestrictionScope.SPECIFIC_ENTITIES;

        // Skip if order type doesn't match restriction scope
        if (orderType === "TICKET" && !isTicketRestriction) {
          continue;
        }
        if (orderType === "ENTITY" && !isEntityRestriction) {
          continue;
        }

        // Check if this item matches the restriction scope
        if (
          matchesRestrictionScope(
            itemId,
            restriction.scope,
            restriction.targetType,
            restriction.targetId,
            orderType
          )
        ) {
          newItemCount += itemQty.quantity;
        }
      }

      // Only validate if there are items being purchased that match this restriction
      if (newItemCount === 0) {
        continue; // Skip this restriction if no matching items in the purchase
      }

      // If no profileId, we can only validate the current selection against the limit
      // (can't count existing items without a profile - profile will be connected when order is completed)
      if (!profileId) {
        // For first-time buyers without profile, check if purchase exceeds limit
        if (newItemCount > restriction.maxQuantity) {
          const restrictionTypeLabel =
            restriction.type === RestrictionType.MAX_TICKETS_PER_EMAIL
              ? "email"
              : restriction.type === RestrictionType.MAX_TICKETS_PER_PHONE
              ? "phone"
              : "profile";

          const itemLabel = orderType === "TICKET" ? "ticket(s)" : "item(s)";

          return {
            valid: false,
            error: `Maximum ${restriction.maxQuantity} ${itemLabel} per ${restrictionTypeLabel} allowed. You are trying to purchase ${newItemCount} ${itemLabel}.`,
          };
        }
        continue; // Move to next restriction
      }

      // Count existing items for this profile identifier
      // This is done after checking newItemCount to avoid unnecessary DB queries
      if (!profile) {
        continue;
      }

      const cacheKey = [
        restriction.eventId,
        restriction.type,
        restriction.scope,
        restriction.targetType ?? "all",
        restriction.targetId ?? "all",
        orderType,
        profile.email ?? "",
        profile.phoneNumber ?? "",
      ].join(":");
      const existingItemCountPromise =
        existingCountCache.get(cacheKey) ??
        countItemsForProfile(
          restriction.eventId,
          restriction.type,
          profile,
          restriction.scope,
          restriction.targetType,
          restriction.targetId,
          orderType
        );

      existingCountCache.set(cacheKey, existingItemCountPromise);
      const existingItemCount = await existingItemCountPromise;

      // Check if total exceeds the limit
      const totalItems = existingItemCount + newItemCount;

      const itemLabel = orderType === "TICKET" ? "ticket(s)" : "item(s)";

      if (totalItems > restriction.maxQuantity) {
        const restrictionTypeLabel =
          restriction.type === RestrictionType.MAX_TICKETS_PER_EMAIL
            ? "email"
            : restriction.type === RestrictionType.MAX_TICKETS_PER_PHONE
            ? "phone"
            : "profile";

        return {
          valid: false,
          error: `Maximum ${restriction.maxQuantity} ${itemLabel} per ${restrictionTypeLabel} allowed. You already have ${existingItemCount} ${itemLabel} and are trying to purchase ${newItemCount} more.`,
        };
      }
    }

    return { valid: true };
  } catch (error: any) {
    console.error("Error validating order restrictions:", error);
    // Don't block order creation if validation fails - log and allow
    return { valid: true };
  }
};

/**
 * Count existing items (tickets or entities) for a profile identifier
 */
const countItemsForProfile = async (
  eventId: string,
  restrictionType: RestrictionType,
  profile: ProfileIdentifier,
  scope: RestrictionScope,
  targetType: RestrictionTargetType | null,
  targetId: string | null,
  orderType: "TICKET" | "ENTITY"
): Promise<number> => {
  try {
    // Build where clause based on restriction type
    const profileWhere: any = {};
    let canApplyRestriction = false;

    if (restrictionType === RestrictionType.MAX_TICKETS_PER_EMAIL) {
      if (!profile.email) {
        // No email, can't apply email-based restriction - return 0 (no existing tickets)
        return 0;
      }
      profileWhere.email = profile.email;
      canApplyRestriction = true;
    } else if (restrictionType === RestrictionType.MAX_TICKETS_PER_PHONE) {
      if (!profile.phoneNumber) {
        // No phone, can't apply phone-based restriction - return 0 (no existing tickets)
        return 0;
      }
      profileWhere.phoneNumber = profile.phoneNumber;
      canApplyRestriction = true;
    } else if (restrictionType === RestrictionType.MAX_TICKETS_PER_PROFILE) {
      // For profile restriction, check both email and phone
      const conditions: any[] = [];
      if (profile.email) {
        conditions.push({ email: profile.email });
      }
      if (profile.phoneNumber) {
        conditions.push({ phoneNumber: profile.phoneNumber });
      }
      if (conditions.length === 0) {
        // No email or phone, can't apply profile-based restriction
        return 0;
      }
      profileWhere.OR = conditions;
      canApplyRestriction = true;
    } else {
      // Unknown restriction type
      return 0;
    }

    if (!canApplyRestriction) {
      return 0;
    }

    // Build order item where clause based on scope and order type
    const orderItemWhere: any = {
      order: {
        eventId,
        status: {
          in: ["COMPLETED", "ON_GOING"], // Count both completed and ongoing orders
        },
        profile: profileWhere,
      },
    };

    // Filter by order type (TICKET or ENTITY)
    if (orderType === "TICKET") {
      // Only count order items with ticketId (not entityId)
      orderItemWhere.ticketId = { not: null };
    } else if (orderType === "ENTITY") {
      // Only count order items with entityId (not ticketId)
      orderItemWhere.entityId = { not: null };
    }

    // Add scope-based filtering using targetType/targetId
    if (scope === RestrictionScope.SPECIFIC_TICKETS && targetType && targetId) {
      if (targetType === RestrictionTargetType.TICKET) {
        orderItemWhere.ticketId = targetId;
      }
    } else if (
      scope === RestrictionScope.SPECIFIC_ENTITIES &&
      targetType &&
      targetId
    ) {
      if (targetType === RestrictionTargetType.ENTITY) {
        orderItemWhere.entityId = targetId;
      }
    } else if (
      scope === RestrictionScope.TICKET_CATEGORY &&
      targetType &&
      targetId
    ) {
      if (targetType === RestrictionTargetType.TICKET_CATEGORY) {
        orderItemWhere.ticket = {
          ticketCategoryId: targetId,
        };
      }
    }
    // For ALL_TICKETS and ALL_ENTITIES, no additional filtering needed

    // Count order items matching the criteria
    const count = await prisma.orderItem.count({
      where: orderItemWhere,
    });

    return count;
  } catch (error: any) {
    console.error("Error counting items for profile:", error);
    return 0;
  }
};

/**
 * Check if an item (ticket or entity) matches the restriction scope
 */
const matchesRestrictionScope = (
  itemId: string,
  scope: RestrictionScope,
  targetType: RestrictionTargetType | null,
  targetId: string | null,
  orderType: "TICKET" | "ENTITY"
): boolean => {
  // Handle ticket scopes
  if (scope === RestrictionScope.ALL_TICKETS) {
    return orderType === "TICKET";
  }

  if (scope === RestrictionScope.SPECIFIC_TICKETS && targetType && targetId) {
    // Check if ticket ID matches the target
    return (
      orderType === "TICKET" &&
      targetType === RestrictionTargetType.TICKET &&
      targetId === itemId
    );
  }

  if (scope === RestrictionScope.TICKET_CATEGORY) {
    // For TICKET_CATEGORY, we'd need to fetch the ticket to check its category
    // This is handled in the countItemsForProfile function
    return orderType === "TICKET"; // Will be filtered in the database query
  }

  // Handle entity scopes
  if (scope === RestrictionScope.ALL_ENTITIES) {
    return orderType === "ENTITY";
  }

  if (scope === RestrictionScope.SPECIFIC_ENTITIES && targetType && targetId) {
    // Check if entity ID matches the target
    return (
      orderType === "ENTITY" &&
      targetType === RestrictionTargetType.ENTITY &&
      targetId === itemId
    );
  }

  return false;
};
